import * as i0 from '@angular/core';
import { Directive, InjectionToken } from '@angular/core';
import { Subject, BehaviorSubject, map, ReplaySubject } from 'rxjs';
import { tuiCreateToken, tuiProvideOptions, tuiCreateTokenFromFactory, tuiTypedFromEvent } from '@taiga-ui/cdk';
import { tuiExtractI18n } from '@taiga-ui/i18n';
const TUI_EDITOR_DEFAULT_EDITOR_COLORS = new Map([['color-black-100', '#909090'], ['color-black-200', '#666666'], ['color-black-300', '#333333'], ['color-blue-100', '#1771e6'], ['color-blue-200', '#1464cc'], ['color-blue-300', '#0953b3'], ['color-gray-100', '#f5f5f6'], ['color-gray-200', '#e7e8ea'], ['color-gray-300', '#cbcfd3'], ['color-gray-400', '#959ba4'], ['color-gray-500', '#79818c'], ['color-gray-600', '#616871'], ['color-green-100', '#39b54a'], ['color-green-200', '#2ca53a'], ['color-green-300', '#168a21'], ['color-light-blue-100', '#ecf1f7'], ['color-light-blue-200', '#e4ebf3'], ['color-light-blue-300', '#dde4ed'], ['color-red-100', '#e01f19'], ['color-red-200', '#d3120e'], ['color-red-300', '#c40b08'], ['color-yellow-100', '#FFDD2C'], ['color-yellow-200', '#FCC521'], ['color-yellow-300', '#FAB618'], ['transparent', 'transparent']]);
const EDITOR_BLANK_COLOR = 'rgb(51, 51, 51)';
const TuiEditorTool = {
  Align: 'justify',
  Anchor: 'anchor',
  Attach: 'attach',
  Bold: 'bold',
  Clear: 'clear',
  Code: 'code',
  Color: 'foreColor',
  Details: 'details',
  Group: 'group',
  HR: 'insertHorizontalRule',
  Hilite: 'hiliteColor',
  Img: 'image',
  Italic: 'italic',
  Link: 'link',
  List: 'list',
  MergeCells: 'mergeCells',
  Quote: 'quote',
  RowsColumnsManaging: 'rowsColumnsManaging',
  Size: 'fontSize',
  SplitCells: 'splitCells',
  Strikethrough: 'strikeThrough',
  Sub: 'subscript',
  Sup: 'superscript',
  Table: 'insertTable',
  CellColor: 'cellColor',
  Tex: 'tex',
  Underline: 'underline',
  Undo: 'undo'
};
const TUI_EDITOR_DEFAULT_TOOLS = new Set([TuiEditorTool.Align, TuiEditorTool.Anchor, TuiEditorTool.Bold, TuiEditorTool.CellColor, TuiEditorTool.Clear, TuiEditorTool.Code, TuiEditorTool.Color, TuiEditorTool.Details, TuiEditorTool.Hilite, TuiEditorTool.HR, TuiEditorTool.Img, TuiEditorTool.Italic, TuiEditorTool.Link, TuiEditorTool.List, TuiEditorTool.Quote, TuiEditorTool.Size, TuiEditorTool.Strikethrough, TuiEditorTool.Sub, TuiEditorTool.Sup, TuiEditorTool.Table, TuiEditorTool.Underline, TuiEditorTool.Undo]);
const TUI_EDITOR_RESIZE_EVENT = 'tui_editor_resize';
function tuiDefaultFontOptionsHandler(texts) {
  return [{
    px: 13,
    name: texts.small
  }, {
    px: 15,
    name: texts.normal
  }, {
    px: 17,
    name: texts.large
  }, {
    px: 24,
    family: 'var(--tui-font-heading)',
    name: texts.subtitle,
    headingLevel: 2,
    weight: 'bold'
  }, {
    px: 30,
    family: 'var(--tui-font-heading)',
    name: texts.title,
    headingLevel: 1,
    weight: 'bold'
  }];
}
const TUI_DEFAULT_HTML5_MEDIA_ATTRIBUTES = ['id', 'class', 'src', 'style', 'controls', 'loop', 'muted', 'preload', 'autoplay', 'width', 'height', 'controlsList'];
const TUI_EDITOR_LINK_HASH_PREFIX = '#';
const TUI_EDITOR_LINK_HTTP_PREFIX = 'http://';
const TUI_EDITOR_LINK_HTTPS_PREFIX = 'https://';
const TUI_EDITOR_LINK_SIMPLE_PROTOCOL_DIVIDER = ':';
const TUI_EDITOR_LINK_OSI_PROTOCOL_DIVIDER = '://';
const TUI_DEFAULT_LINK_OPTIONS = {
  protocol: TUI_EDITOR_LINK_HTTPS_PREFIX
};
let AbstractTuiEditor = /*#__PURE__*/(() => {
  class AbstractTuiEditor {
    constructor() {
      this.stateChange$ = new Subject();
      this.valueChange$ = new BehaviorSubject('');
    }
    static {
      this.ɵfac = function AbstractTuiEditor_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AbstractTuiEditor)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: AbstractTuiEditor,
        standalone: false
      });
    }
  }
  return AbstractTuiEditor;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Extensions for editor
 */
const TUI_EDITOR_EXTENSIONS = new InjectionToken('[TUI_EDITOR_EXTENSIONS]');
/**
 * lazy extensions
 */
const LAZY_EDITOR_EXTENSIONS = new InjectionToken('[LAZY_EDITOR_EXTENSIONS]');
const TUI_EDITOR_DEFAULT_OPTIONS = {
  translate: 'no',
  spellcheck: false,
  exampleText: '',
  placeholder: '',
  enableDefaultStyles: true,
  tools: TUI_EDITOR_DEFAULT_TOOLS,
  colors: TUI_EDITOR_DEFAULT_EDITOR_COLORS,
  blankColor: EDITOR_BLANK_COLOR,
  linkOptions: TUI_DEFAULT_LINK_OPTIONS,
  fontOptions: tuiDefaultFontOptionsHandler,
  floatingToolbar: false,
  parseOptions: {},
  icons: {
    undo: '@tui.undo',
    redo: '@tui.redo',
    quote: '@tui.quote',
    link: '@tui.link',
    anchor: '@tui.anchor',
    attach: '@tui.paperclip',
    sub: '@tui.subscript',
    sup: '@tui.superscript',
    tex: '@tui.whole-word',
    image: '@tui.image',
    hr: '@tui.minus',
    clear: '@tui.remove-formatting',
    groupAdd: '@tui.plus',
    groupRemove: '@tui.circle-minus',
    detailsAdd: '@tui.copy-plus',
    detailsRemove: '@tui.square-minus',
    popupLinkSave: '@tui.save',
    popupLinkRemove: '@tui.x',
    popupPreviewLinkEdit: '@tui.pencil',
    popupPreviewLinkClear: '@tui.x',
    paint: '@tui.paint-bucket',
    hash: '@tui.hash',
    externalLink: '@tui.external-link',
    textAlignPreview: '@tui.align-left',
    textAlignLeft: '@tui.align-left',
    textAlignCenter: '@tui.align-center',
    textAlignRight: '@tui.align-right',
    textAlignJustify: '@tui.align-justify',
    textColor: '@tui.baseline',
    textHilite: '@tui.paint-roller',
    listPreview: '@tui.list',
    listUnOrdered: '@tui.list',
    listOrdered: '@tui.list-ordered',
    taskList: '@tui.check-check',
    indent: '@tui.indent-increase',
    outdent: '@tui.indent-decrease',
    fontSize: '@tui.a-large-small',
    insertTable: '@tui.table',
    tableCellMerge: '@tui.table-cells-merge',
    tableCellSplit: '@tui.table-rows-split',
    addRowTable: '@tui.between-horizontal-start',
    code: '@tui.code',
    fontStylePreview: '@tui.type-outline',
    fontStyleBold: '@tui.bold',
    fontStyleItalic: '@tui.italic',
    fontStyleUnderline: '@tui.underline',
    fontStyleStrike: '@tui.strikethrough',
    colorSelectorDropdownChevron: '@tui.chevron-down',
    colorSelectorDropdownCheck: '@tui.check',
    imageExtension: {
      settings: '@tui.settings',
      alignJustify: '@tui.align-justify',
      alignCenter: '@tui.align-center',
      alignLeft: '@tui.align-left',
      alignRight: '@tui.align-right'
    }
  }
};
const TUI_EDITOR_OPTIONS = tuiCreateToken(TUI_EDITOR_DEFAULT_OPTIONS);
function tuiEditorOptionsProvider(options) {
  return tuiProvideOptions(TUI_EDITOR_OPTIONS, options, TUI_EDITOR_DEFAULT_OPTIONS);
}
const TUI_EDITOR_SANITIZER = tuiCreateTokenFromFactory(() => null);
const TUI_EDITOR_VALUE_TRANSFORMER = new InjectionToken('[TUI_EDITOR_VALUE_TRANSFORMER]');

/**
 * files loader options
 */
const TUI_ATTACH_FILES_OPTIONS = new InjectionToken('[TUI_ATTACH_FILES_OPTIONS]', {
  factory: () => ({
    accept: '*/*',
    multiple: true
  })
});
/**
 * files loader handler
 */
const TUI_ATTACH_FILES_LOADER = new InjectionToken('[TUI_ATTACH_FILES_LOADER]');
const TUI_TIPTAP_WHITESPACE_HACK = '<span style="font-size: 15px"> </span>'; // require: `@tiptap/extension-text-style`

/**
 * tui-color-selector i18n
 */
const TUI_EDITOR_COLOR_SELECTOR_MODE_NAMES = new InjectionToken('[TUI_EDITOR_COLOR_SELECTOR_MODE_NAMES]', {
  factory: () => ['Solid color', 'Gradient']
});
/**
 * tui-editor-toolbar i18n
 */
const TUI_EDITOR_TOOLBAR_TEXTS = new InjectionToken('[TUI_EDITOR_TOOLBAR_TEXTS]', {
  factory: tuiExtractI18n('toolbarTools')
});
/**
 * tui-editor-toolbar table i18n
 */
const TUI_EDITOR_TABLE_COMMANDS = new InjectionToken('[TUI_EDITOR_TABLE_COMMANDS]', {
  factory: tuiExtractI18n('editorTableCommands')
});
/**
 * tui-editor-toolbar edit-link i18n
 */
const TUI_EDITOR_LINK_TEXTS = new InjectionToken('[TUI_EDITOR_LINK_TEXTS]', {
  factory: tuiExtractI18n('editorEditLink')
});
/**
 * tui-editor-toolbar codes options
 */
const TUI_EDITOR_CODE_OPTIONS = new InjectionToken('[TUI_EDITOR_CODE_OPTIONS]', {
  factory: tuiExtractI18n('editorCodeOptions')
});
/**
 * tui-editor-toolbar font options
 */
const TUI_EDITOR_FONT_OPTIONS = new InjectionToken('[TUI_EDITOR_FONT_OPTIONS]', {
  factory: tuiExtractI18n('editorFontOptions')
});

/**
 * Image loader handler
 */
const TUI_IMAGE_LOADER = new InjectionToken('[TUI_IMAGE_LOADER]', {
  factory: () => file => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    return tuiTypedFromEvent(fileReader, 'load').pipe(map(() => String(fileReader.result)));
  }
});

/**
 * Token for Tiptap Editor
 */
const TIPTAP_EDITOR = new InjectionToken('[TIPTAP_EDITOR]');
/**
 * Lazy loaded Editor
 */
const LAZY_TIPTAP_EDITOR = new InjectionToken('[LAZY_TIPTAP_EDITOR]', {
  factory: () => {
    const editor$ = new ReplaySubject(1);
    import('@tiptap/core').then(({
      Editor
    }) => editor$.next(Editor)).catch(() => editor$.complete());
    return editor$;
  }
});
/**
 * The container in which the tip-tap editor is initialized
 */
const INITIALIZATION_TIPTAP_CONTAINER = new InjectionToken('[INITIALIZATION_TIPTAP_CONTAINER]');

/**
 * Generated bundle index. Do not edit.
 */

export { AbstractTuiEditor, EDITOR_BLANK_COLOR, INITIALIZATION_TIPTAP_CONTAINER, LAZY_EDITOR_EXTENSIONS, LAZY_TIPTAP_EDITOR, TIPTAP_EDITOR, TUI_ATTACH_FILES_LOADER, TUI_ATTACH_FILES_OPTIONS, TUI_DEFAULT_HTML5_MEDIA_ATTRIBUTES, TUI_DEFAULT_LINK_OPTIONS, TUI_EDITOR_CODE_OPTIONS, TUI_EDITOR_COLOR_SELECTOR_MODE_NAMES, TUI_EDITOR_DEFAULT_EDITOR_COLORS, TUI_EDITOR_DEFAULT_OPTIONS, TUI_EDITOR_DEFAULT_TOOLS, TUI_EDITOR_EXTENSIONS, TUI_EDITOR_FONT_OPTIONS, TUI_EDITOR_LINK_HASH_PREFIX, TUI_EDITOR_LINK_HTTPS_PREFIX, TUI_EDITOR_LINK_HTTP_PREFIX, TUI_EDITOR_LINK_OSI_PROTOCOL_DIVIDER, TUI_EDITOR_LINK_SIMPLE_PROTOCOL_DIVIDER, TUI_EDITOR_LINK_TEXTS, TUI_EDITOR_OPTIONS, TUI_EDITOR_RESIZE_EVENT, TUI_EDITOR_SANITIZER, TUI_EDITOR_TABLE_COMMANDS, TUI_EDITOR_TOOLBAR_TEXTS, TUI_EDITOR_VALUE_TRANSFORMER, TUI_IMAGE_LOADER, TUI_TIPTAP_WHITESPACE_HACK, TuiEditorTool, tuiDefaultFontOptionsHandler, tuiEditorOptionsProvider };
